import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import { Close, Description, PictureAsPdf, InsertDriveFile } from '@mui/icons-material';
import { getDataWAuth } from '../../utils/api-service';
import { EXTENSION_DOCUMENTS_URI } from '../../constants/constant';

const DocumentModal = ({ open, onClose, assignmentNo }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && assignmentNo) {
      fetchDocuments();
    }
  }, [open, assignmentNo]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getDataWAuth(`${EXTENSION_DOCUMENTS_URI}/`, {
        "AssignmentNumber": assignmentNo
      });
      const list = response.data?.documentRequestDTOList || [];
      if (response.status === 200 && list.length > 0) {
        setDocuments(list);
      } else {
        setError('No documents found');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.response?.data?.message || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const openDocument = (doc) => {
   const url = doc?.url;
  if (url) {
    window.open(url, '_blank');
  }
  };

  const getDocumentIcon = (docType) => {
    if (docType === 'PDF') {
      return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
    } else if (docType === 'DOC' || docType === 'DOCX') {
      return <Description sx={{ color: '#1976d2' }} />;
    } else {
      return <InsertDriveFile sx={{ color: '#757575' }} />;
    }
  };

  const getDocumentName = (index, totalDocs) => {
    if (index === 0) {
      return 'Billing Document PDF';
    } else if (index === 1) {
      return 'Billing Document PDF (Duplicate)';
    } else {
      return 'PDF';
    }
  };

  const handleClose = () => {
    setDocuments([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Documents for Assignment Number: {assignmentNo}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box textAlign="center" alignItems="center" py={20}>
            <Typography color="error" variant="h6">{error}</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Related Documents
            </Typography>

           {documents.length > 0 ? (
  <List>
    {documents.map((doc, index) => (
      <React.Fragment key={index}>
        <ListItem
          button
          onClick={() => openDocument(doc)}
          sx={{
            borderRadius: 1,
            mb: 1,
            '&:hover': {
              backgroundColor: '#f5f5f5',
              boxShadow: 1,
            },
            border: '1px solid #e0e0e0',
            cursor: 'pointer',
          }}
        >
          <ListItemIcon>{getDocumentIcon(doc.docType)}</ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {getDocumentName(index, documents.length)}
              </Typography>
            }
            secondary="Click to view document"
          />
        </ListItem>
      </React.Fragment>
    ))}
  </List>
) : (
  <Box
    textAlign="center"
    py={4}
    sx={{
      backgroundColor: '#f9f9f9',
      borderRadius: 1,
      border: '1px dashed #ccc',
    }}
  >
    <Typography color="textSecondary">
      No documents available for this assignment number.
    </Typography>
  </Box>
)}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentModal;
